/**
 * Phase 8 · C-33 — Career-page branding service.
 *
 * Replaces the `career_primary_<orgId>`, `career_secondary_<orgId>`, and
 * `career_tagline_<orgId>` localStorage buckets. Reads/writes a single
 * row per org in `ggj_b2b_careers_brand` (see migration 103).
 *
 * The DB column is called `accent_color`; the UI still labels it
 * "Secondary colour". We expose it as `accentColor` in TypeScript so the
 * naming stays honest at the storage layer.
 */
import { supabase } from '../client';

export interface CareersBrand {
  orgId: string;
  primaryColor: string | null;
  accentColor: string | null;
  tagline: string | null;
  logoUrl: string | null;
  updatedAt: string | null;
}

export interface CareersBrandInput {
  primaryColor?: string | null;
  accentColor?: string | null;
  tagline?: string | null;
  logoUrl?: string | null;
}

interface CareersBrandRow {
  org_id: string;
  primary_color: string | null;
  accent_color: string | null;
  tagline: string | null;
  logo_url: string | null;
  updated_at: string | null;
}

function rowToBrand(row: CareersBrandRow): CareersBrand {
  return {
    orgId: row.org_id,
    primaryColor: row.primary_color,
    accentColor: row.accent_color,
    tagline: row.tagline,
    logoUrl: row.logo_url,
    updatedAt: row.updated_at,
  };
}

function emptyBrand(orgId: string): CareersBrand {
  return {
    orgId,
    primaryColor: null,
    accentColor: null,
    tagline: null,
    logoUrl: null,
    updatedAt: null,
  };
}

export const careersBrandService = {
  /**
   * Fetch the branding row for an org. Returns a zero-state when the row
   * doesn't exist yet — the page treats "no row" and "empty fields" the same.
   */
  async get(orgId: string): Promise<CareersBrand> {
    if (!orgId) return emptyBrand('');

    const { data, error } = await supabase
      .from('ggj_b2b_careers_brand')
      .select('org_id, primary_color, accent_color, tagline, logo_url, updated_at')
      .eq('org_id', orgId)
      .maybeSingle();

    if (error) throw new Error(error.message);
    if (!data) return emptyBrand(orgId);
    return rowToBrand(data as CareersBrandRow);
  },

  /**
   * Upsert the branding row. Unspecified fields are left untouched by reusing
   * the current server state (mutating via a single UPSERT avoids a second
   * round-trip). Returns the final persisted row so callers can reconcile
   * their optimistic cache with the trigger-stamped `updated_at`.
   */
  async save(orgId: string, input: CareersBrandInput): Promise<CareersBrand> {
    if (!orgId) throw new Error('orgId is required to save branding.');

    // Read-modify-write: PG's default UPSERT overwrites unspecified NULLs, which
    // would clobber an already-uploaded logo when only the colours change.
    const current = await this.get(orgId);

    const payload = {
      org_id: orgId,
      primary_color: input.primaryColor !== undefined ? input.primaryColor : current.primaryColor,
      accent_color:  input.accentColor  !== undefined ? input.accentColor  : current.accentColor,
      tagline:       input.tagline      !== undefined ? input.tagline      : current.tagline,
      logo_url:      input.logoUrl      !== undefined ? input.logoUrl      : current.logoUrl,
    };

    const { data, error } = await supabase
      .from('ggj_b2b_careers_brand')
      .upsert(payload, { onConflict: 'org_id' })
      .select('org_id, primary_color, accent_color, tagline, logo_url, updated_at')
      .single();

    if (error) throw new Error(error.message);
    return rowToBrand(data as CareersBrandRow);
  },
};
